import type {
  CalculationFilters,
  CalculationStatus,
} from "./CalculationInputList.types";
import {
  defaultPageSize,
  emptyFilters,
  pageSizeOptions,
} from "./CalculationInputList.constants";

const validStatuses: CalculationStatus[] = ["DRAFT", "COMPLETE"];
const integerPattern = /^-?\d+$/;

export interface CalculationListPaginationState {
  currentPage: number;
  pageSize: number;
}

function parseInteger(raw: string | null): number | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!integerPattern.test(trimmed)) return null;
  const n = Number(trimmed);
  if (!Number.isSafeInteger(n)) return null;
  return n;
}

function validYear(raw: string | null): number | null {
  const n = parseInteger(raw);
  if (n === null) return null;
  const currentYear = new Date().getFullYear();
  if (n < currentYear - 1 || n > currentYear + 2) return null;
  return n;
}

function validCurrentPage(raw: string | null): number {
  const n = parseInteger(raw);
  if (n === null || n < 1) return 0;
  return n - 1;
}

function validPageSize(raw: string | null): number {
  const n = parseInteger(raw);
  if (n === null) return defaultPageSize;
  return pageSizeOptions.includes(n) ? n : defaultPageSize;
}

export function filtersToSearchParams(
  filters: CalculationFilters,
): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.name) params.set("name", filters.name);
  filters.lines.forEach((l) => params.append("lines", l));
  filters.supplierIds.forEach((s) => params.append("suppliers", s));
  filters.kamNames.forEach((k) => params.append("kams", k));
  if (filters.planningYear !== null)
    params.set("year", String(filters.planningYear));
  if (filters.status !== null) params.set("status", filters.status);
  return params;
}

export function searchParamsToFilters(
  params: URLSearchParams,
): CalculationFilters {
  const rawYear = params.get("year");
  const rawStatus = params.get("status");

  const status = validStatuses.includes(rawStatus as CalculationStatus)
    ? (rawStatus as CalculationStatus)
    : null;

  return {
    ...emptyFilters,
    name: params.get("name") ?? "",
    lines: params.getAll("lines"),
    supplierIds: params.getAll("suppliers"),
    kamNames: params.getAll("kams"),
    planningYear: validYear(rawYear),
    status,
  };
}

export function searchParamsToPagination(
  params: URLSearchParams,
): CalculationListPaginationState {
  return {
    currentPage: validCurrentPage(params.get("page")),
    pageSize: validPageSize(params.get("pageSize")),
  };
}

export function listStateToSearchParams(
  filters: CalculationFilters,
  currentPage: number,
  pageSize: number,
): URLSearchParams {
  const params = filtersToSearchParams(filters);
  if (currentPage > 0) params.set("page", String(currentPage + 1));
  if (pageSize !== defaultPageSize) params.set("pageSize", String(pageSize));
  return params;
}
